// The rule of the server-side guard for staff messages (CHT-732; docs/engineering/messaging.md). The staff web UI hides the composer of a
// staff member who does not own the handoff, but a stale tab, a resend after a release or a hand-made request can still write into the
// customer channel. Stream calls the before-send hook for every message, and the message projection checks again after the fact. Both use
// THIS rule, so the answer is the same on both paths. It is pure: the caller reads the facts from Postgres at that moment.
/** Why a staff message is refused. A closed list: it goes into the text the staff user sees, into the audit row and into the logs. */
export const staffMessageRefusals = Object.freeze([
    /** The conversation is not `HUMAN_ACTIVE`: no person is in charge (the AI, a request or a waiting handoff). */
    "NOT_HUMAN_ACTIVE",
    /** `HUMAN_ACTIVE`, but no accepted handoff is open (a read that could not be explained: refuse). */
    "NO_ACCEPTED_HANDOFF",
    /** Another staff user owns the accepted handoff (the writer lost it, or never had it). */
    "NOT_HANDOFF_OWNER",
    /** The staff user does not exist (a stale id) or is deactivated. */
    "STAFF_NOT_ACTIVE",
    /** The staff user is active but holds neither `concierge` nor `supervisor`. */
    "STAFF_ROLE_MISSING",
    /** The conversation or its channel binding is gone. */
    "CONVERSATION_UNKNOWN",
    /** The database could not answer in time: staff messages fail CLOSED. */
    "LOOKUP_UNAVAILABLE",
]);
/** The words the staff user sees when the hook refuses a message. Fixed, no id and no detail. */
export const STAFF_MESSAGE_REFUSED_TEXT = "You can no longer write in this conversation";
/** The text of the refusal as sent to Stream: the fixed sentence and the closed code. */
export const staffMessageRefusedText = (refusal) => `${STAFF_MESSAGE_REFUSED_TEXT} (code: ${refusal})`;
// --- the deadline of the before-send hook (CHT-732 review) ---------------------------------------------------------------------
/**
 * The per-attempt timeout that `scripts/stream-configure.mjs` writes into Stream for the before-send hook, in ms (the setting
 * `before_message_send_hook_attempt_timeout_ms`). Stream allows 1 to 5000, makes a single attempt and FAILS OPEN when it is exceeded.
 */
export const BEFORE_SEND_HOOK_TIMEOUT_MS = 2_000;
export const BEFORE_SEND_HOOK_TIMEOUT_MAX_MS = 5_000;
/** What the guard keeps of the timeout for the network, the answer and a slow start: it decides by `timeout - margin`. */
export const BEFORE_SEND_DEADLINE_MARGIN_MS = 400;
/** What the database needs for the shared lock and the three reads in the worst healthy case. */
export const BEFORE_SEND_READ_BUDGET_MS = 1_500;
/** The least timeout that leaves the database its read budget. The script refuses a lower one. */
export const BEFORE_SEND_HOOK_TIMEOUT_MIN_MS = BEFORE_SEND_READ_BUDGET_MS + BEFORE_SEND_DEADLINE_MARGIN_MS;
/**
 * How long the guard waits for its reads, from the timeout configured in Stream: the timeout minus the margin. A timeout outside
 * 1,900 to 5,000 ms is a configuration error (below it a healthy database would make the guard fail closed).
 */
export function beforeSendDeadlineMs(hookTimeoutMs) {
    if (!Number.isInteger(hookTimeoutMs) ||
        hookTimeoutMs < BEFORE_SEND_HOOK_TIMEOUT_MIN_MS ||
        hookTimeoutMs > BEFORE_SEND_HOOK_TIMEOUT_MAX_MS) {
        throw new RangeError(`the before-send hook timeout must be an integer from ${BEFORE_SEND_HOOK_TIMEOUT_MIN_MS} to ${BEFORE_SEND_HOOK_TIMEOUT_MAX_MS} ms`);
    }
    return hookTimeoutMs - BEFORE_SEND_DEADLINE_MARGIN_MS;
}
/** The roles that may write to a customer. The same two roles that get a Stream token (`StaffMessagingEligible`). */
export const STAFF_MESSAGE_ROLES = Object.freeze(["concierge", "supervisor"]);
/**
 * A staff-authored message in a customer channel is allowed ONLY when all of this holds at the moment of the read: the conversation is
 * `HUMAN_ACTIVE`, an `ACCEPTED` handoff of it is owned by this staff user, and the staff user is `ACTIVE` with the `concierge` or the
 * `supervisor` role. Every other case is a refusal with one closed code. The first failing question names the code, in this order.
 */
export function decideStaffMessage(facts) {
    if (facts.conversationMode === null)
        return { allowed: false, refusal: "CONVERSATION_UNKNOWN" };
    if (facts.staff === null || facts.staff.status !== "ACTIVE") {
        return { allowed: false, refusal: "STAFF_NOT_ACTIVE" };
    }
    if (!facts.staff.roles.some((role) => STAFF_MESSAGE_ROLES.includes(role))) {
        return { allowed: false, refusal: "STAFF_ROLE_MISSING" };
    }
    if (facts.conversationMode !== "HUMAN_ACTIVE")
        return { allowed: false, refusal: "NOT_HUMAN_ACTIVE" };
    if (facts.handoff === null || facts.handoff.status !== "ACCEPTED") {
        return { allowed: false, refusal: "NO_ACCEPTED_HANDOFF" };
    }
    if (facts.handoff.acceptedByStaffUserId !== facts.authorStaffUserId) {
        return { allowed: false, refusal: "NOT_HANDOFF_OWNER" };
    }
    return { allowed: true };
}
